import logger from "../utils/logger";
// src/utils/lazyWithRetry.js
import React, { lazy } from "react";

const STALE_CHUNK_RELOAD_KEY = "synexos_stale_chunk_reload_ts";
const RELOAD_COOLDOWN_MS = 20000; // 20-second cooldown prevents infinite reload loops

/**
 * Checks if sessionStorage is available and working.
 */
function isSessionStorageAvailable() {
  try {
    const testKey = "__st_test__";
    window.sessionStorage.setItem(testKey, testKey);
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Detects whether an error is caused by a missing, stale, or failed frontend chunk.
 * 
 * Matches:
 * - Webpack: ChunkLoadError, "Loading chunk [id] failed"
 * - Vite / Rollup / Native ESM: "Failed to fetch dynamically imported module", "Importing a module script failed"
 * - Network failures fetching JS chunk: "Failed to fetch"
 * - SPA Fallback returning HTML (200) instead of JS: SyntaxError: Unexpected token '<'
 */
export function isChunkLoadError(error) {
  if (!error) return false;

  const errorName = error?.name || "";
  const errorMessage = error?.message || String(error);

  if (errorName === "ChunkLoadError") {
    return true;
  }

  const chunkPatterns = [
    /chunkloaderror/i,
    /loading chunk/i,
    /failed to fetch dynamically imported module/i,
    /importing a module script failed/i,
    /error loading dynamically imported module/i,
    /unable to load script/i,
  ];

  if (chunkPatterns.some((pattern) => pattern.test(errorMessage) || pattern.test(errorName))) {
    return true;
  }

  // Detect SyntaxError when the server returns HTML (index.html via SPA fallback) for a deleted chunk .js file
  if (
    error instanceof SyntaxError &&
    (errorMessage.includes("Unexpected token '<'") ||
      errorMessage.includes("expected expression, got '<'") ||
      errorMessage.includes("Unexpected '<'"))
  ) {
    return true;
  }

  return false;
}

/**
 * Attempts a single controlled page reload when a stale chunk is detected.
 * Returns true if reload was initiated, or false if recovery was already exhausted.
 */
export function triggerStaleChunkReload(error) {
  if (typeof window === "undefined") return false;

  try {
    const hasStorage = isSessionStorageAvailable();
    const now = Date.now();

    if (hasStorage) {
      const lastReloadStr = window.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY);
      if (lastReloadStr) {
        const lastReload = parseInt(lastReloadStr, 10);
        if (!isNaN(lastReload) && now - lastReload < RELOAD_COOLDOWN_MS) {
          logger.warn(
            "[STALE_CHUNK_RECOVERY] Automatic reload already executed recently. Aborting reload loop.",
            { timeSinceLastReloadMs: now - lastReload, error }
          );
          return false;
        }
      }
      window.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(now));
    } else {
      // Memory fallback if sessionStorage is blocked
      if (window.__LAST_CHUNK_RELOAD__ && now - window.__LAST_CHUNK_RELOAD__ < RELOAD_COOLDOWN_MS) {
        return false;
      }
      window.__LAST_CHUNK_RELOAD__ = now;
    }

    logger.warn(
      "[STALE_CHUNK_RECOVERY] Stale chunk detected after deployment. Automatically reloading page to fetch latest application...",
      { error: error?.message || error }
    );

    // Perform a clean reload from the server
    window.location.reload();
    return true;
  } catch (e) {
    logger.error("[STALE_CHUNK_RECOVERY] Failed to trigger reload safely:", e?.message || "Unknown error");
    return false;
  }
}

/**
 * Reusable wrapper around React.lazy() that intercepts chunk loading failures,
 * performs an automatic page reload to fetch the latest application bundle,
 * and guards against infinite reload loops.
 * 
 * @param {() => Promise<{ default: React.ComponentType<any> }>} factory
 * @returns {React.LazyExoticComponent<React.ComponentType<any>>}
 */
export function lazyWithRetry(factory) {
  return lazy(() =>
    factory().catch((error) => {
      if (isChunkLoadError(error)) {
        const reloaded = triggerStaleChunkReload(error);
        if (reloaded) {
          // Provide a bounded timeout so Suspense does not hang indefinitely if window reload is delayed
          return new Promise((_, reject) => {
            setTimeout(() => {
              reject(error || new Error("Application reload in progress..."));
            }, 3500);
          });
        }
      }
      // Non-chunk error or reload exhausted: rethrow to nearest ErrorBoundary
      throw error;
    })
  );
}

export default lazyWithRetry;
